import type { KUTCResultEntry } from '../services/kutcResultsService';
import { exportBirthdate, exportGender, exportStatus, ExportIdentity } from './kutcResultExport';
import { kutcDistanceKm, KUTC_LOOP_METERS } from './kutcCourse';
import type { DuvOptionalDetails } from './kutcDuvDetails';
import { isoToIoc } from '../constants/iocCountryCodes';

export const DUV_HEADERS = ['Rank', 'Surname', 'First Name', 'Gender', 'Year of Birth', 'Nationality', 'Performance', 'Date of Birth', 'Age', 'Age category', 'Club', 'Residence', 'Length / Duration', 'Perf. in miles', 'Name in non-latin characters', 'Comment'];
export interface DuvSettings {
  editionId: string;
  eventName: string;
  eventDate: string; // Local race date, YYYY-MM-DD; independent of browser timezone.
  durationHours: number;
  loopMeters: number; // Must equal the exact KUTC definition; never a rounded metadata value.
}
export interface DuvResultRow {
  personId: number;
  rank: number;
  firstName: string;
  lastName: string;
  gender: string;
  yearOfBirth: number | '';
  birthdate: string;
  nationality: string;
  loops: number;
  kilometers: number;
  club?: string;
  residence?: string;
  nameInNonLatinCharacters?: string;
  age?: number;
  issues: string[];
  excluded: string;
  timeSeconds?: number;
}
const nameKey = (r: { firstName?: string; lastName?: string }) => `${r.firstName || ''} ${r.lastName || ''}`.normalize('NFKC').trim().replace(/\s+/g, ' ').toLowerCase();
export function duvSettingsIssues(settings: DuvSettings): string[] {
  const issues: string[] = [];
  if (!/^kutc-\d{4}$/.test(settings.editionId)) issues.push('Velg én KUTC-årsutgave.');
  if (!settings.eventName.trim()) issues.push('Mangler arrangementsnavn.');
  if (!exportBirthdate(settings.eventDate)) issues.push('Mangler gyldig løpsdato.');
  else if (/^kutc-\d{4}$/.test(settings.editionId) && settings.eventDate.slice(0, 4) !== settings.editionId.slice(-4)) issues.push('Løpsdato må tilhøre valgt årsutgave.');
  if (settings.loopMeters !== KUTC_LOOP_METERS) issues.push('Rundelengden må være nøyaktig 100 miles / 24.');
  if (!Number.isFinite(settings.durationHours) || settings.durationHours <= 0) issues.push('Varighet må være et positivt antall timer.');
  return issues;
}

export function duvResultRows(results: KUTCResultEntry[], identities: Record<string, ExportIdentity & DuvOptionalDetails>, settings: DuvSettings,
  distanceRows: { personId: number; firstName: string; lastName: string }[], linkErrors: Record<string, string> = {}): DuvResultRow[] {
  const seen = new Set<number>();
  return results.map(result => {
    const status = exportStatus(result.status);
    const loops = result.loopsCompleted ?? NaN;
    const km = kutcDistanceKm(loops);
    const identity = identities[result.personId] || {};
    const birthdate = exportBirthdate(identity.dateOfBirth);
    const rawYear = birthdate ? birthdate.slice(0, 4) : String(identity.yearOfBirth || '');
    const yearOfBirth = /^\d{4}$/.test(rawYear) && Number(rawYear) > 0 && Number(rawYear) <= Number(settings.eventDate.slice(0, 4)) ? Number(rawYear) : '';
    const row: DuvResultRow = {
      personId: result.personId, rank: result.finalRank ?? NaN, firstName: result.firstName?.trim() || '', lastName: result.lastName?.trim() || '',
      gender: exportGender(identity.gender), birthdate, yearOfBirth, nationality: isoToIoc(identity.nationality),
      loops, kilometers: km, club: identity.club || '', residence: identity.residence || '', nameInNonLatinCharacters: identity.nameInNonLatinCharacters || '',
      age: birthdate && birthdate <= settings.eventDate ? Number(settings.eventDate.slice(0, 4)) - Number(birthdate.slice(0, 4)) - (settings.eventDate.slice(5) < birthdate.slice(5) ? 1 : 0) : undefined,
      issues: [], excluded: ''
    };
    if (status === 'DNS') row.excluded = 'DNS';
    else if (status === 'DSQ') row.excluded = 'Diskvalifisert';
    else if ((status === 'Finished' || status === 'DNF') && Number.isInteger(loops) && loops >= 0 && loops < 7) row.excluded = 'Færre enn 7 fullførte runder';
    if (!Number.isInteger(row.personId) || row.personId <= 0) row.issues.push('Ugyldig person-ID.');
    if (seen.has(row.personId)) row.issues.push('Duplikat person-ID.');
    seen.add(row.personId);
    if (!row.firstName || !row.lastName) row.issues.push('Mangler navn.');
    if (!status) row.issues.push('Ukjent resultatstatus.');
    if (!Number.isInteger(loops) || loops < 0) row.issues.push('Ugyldig antall fullførte runder.');
    if (!Number.isInteger(row.rank) || row.rank <= 0) row.issues.push('Mangler gyldig totalplassering.');
    if (!row.gender) row.issues.push('Mangler kjønn.');
    if (!row.yearOfBirth) row.issues.push('Mangler gyldig fødselsår/fødselsdato.');
    if (birthdate > settings.eventDate) row.issues.push('Fødselsdato er etter løpsdato.');
    if (!row.nationality) row.issues.push('Mangler nasjonalitet som kan konverteres til IOC.');
    if (identity.error) row.issues.push(identity.error);
    if (linkErrors[row.personId]) row.issues.push(linkErrors[row.personId]);
    const matching = distanceRows.filter(r => r.personId === row.personId);
    if (!matching.length || matching.some(r => nameKey(r) !== nameKey(row))) row.issues.push('Totalresultatet samsvarer ikke med identiteten i distanseresultatene.');
    return row;
  }).sort((a, b) => (Number.isFinite(a.rank) ? a.rank : Infinity) - (Number.isFinite(b.rank) ? b.rank : Infinity) || a.personId - b.personId);
}

export interface DuvSection {
  id: string;
  name: string;
  kind: 'distance' | 'los';
  loops?: number;
  rows: DuvResultRow[];
  issues: string[];
}
export function buildDuvSections(total: KUTCResultEntry[], races: { distanceKey: string; name: string; sourceResults?: KUTCResultEntry[] }[],
  identities: Record<string, ExportIdentity & DuvOptionalDetails>, settings: DuvSettings,
  distanceRows: { personId: number; firstName: string; lastName: string }[], linkErrors: Record<string, string> = {}): DuvSection[] {
  const sections: DuvSection[] = [];
  for (const race of races) {
    const match = /^(\d+)-loops$/.exec(race.distanceKey);
    if (!match || Number(match[1]) < 8) continue;
    const loops = Number(match[1]);
    const source = race.sourceResults || [];
    const rows = duvResultRows(source.map(r => ({ ...r, loopsCompleted: loops, finalRank: r.raceRank })), identities, settings, distanceRows, linkErrors);
    rows.forEach(row => {
      const result = source.find(r => r.personId === row.personId)!;
      const status = exportStatus(result.status);
      if (status === 'DNF') row.excluded = 'DNF på distansen';
      row.timeSeconds = typeof result.raceTimeSeconds === 'number' ? result.raceTimeSeconds : NaN;
      if (status === 'Finished' && (!Number.isFinite(row.timeSeconds) || row.timeSeconds! <= 0)) row.issues.push('Mangler gyldig distansetid.');
    });
    sections.push({ id: race.distanceKey, name: race.name, kind: 'distance', loops, rows,
      issues: race.sourceResults ? [] : ['Distansegrunnlaget mangler. Hent resultatene på nytt.'] });
  }
  sections.sort((a, b) => a.loops! - b.loops!);
  sections.push({ id: 'last-one-standing', name: 'Last One Standing', kind: 'los',
    rows: duvResultRows(total, identities, settings, distanceRows, linkErrors), issues: [] });
  return sections;
}
export type DuvCell = string | number | null | { value: string | number | Date; format?: string; fontWeight?: 'bold'; backgroundColor?: string };
/** English template: rows 1–5 metadata, headers 8, results 9+. Section markers in M. */
export function duvWorkbookData(settings: DuvSettings, sections: DuvSection[]): DuvCell[][] {
  const problems = duvSettingsIssues(settings);
  if (problems.length) throw new Error(problems.join(' '));
  if (sections.some(section => section.issues.length)) throw new Error('Distansegrunnlaget mangler. Hent resultatene på nytt.');
  const populated = sections.map(section => ({ ...section, rows: section.rows.filter(row => !row.excluded) })).filter(section => section.rows.length);
  if (!populated.length) throw new Error('Ingen DUV-resultater på minst 45 km.');
  const dateCell = (date: string) => ({ value: new Date(`${date}T00:00:00Z`), format: 'dd.mm.yyyy' });
  const label = (value: string) => ({ value, fontWeight: 'bold' as const });
  const input = (value: string) => ({ value, backgroundColor: '#FFFF99' });
  const output: DuvCell[][] = [
    ['Select your language', null, 'English'],
    ['Name of Event', null, input(populated.length === 1 ? `${settings.eventName.trim()} – ${populated[0].name}` : settings.eventName.trim())],
    ['Date', null, { ...dateCell(settings.eventDate), backgroundColor: '#FFFF99' }],
    ['Length/Duration', null, input(populated.length === 1 ? (populated[0].kind === 'los' ? `${settings.durationHours} h` : `${kutcDistanceKm(populated[0].loops!)} km`) : 'Distances and Last One Standing – see column M')],
    ['Data source', null, 'from race director'],
    populated.some(section => section.kind === 'los') ? ["Value in 'Length / Duration' column is length in km"] : [],
    [label('Mandatory fields'), null, null, null, null, null, null, label('Optional fields')], DUV_HEADERS.map(label)
  ];
  for (const section of populated) {
    const seen = new Set<number>();
    for (let i = 0; i < section.rows.length; i++) {
      const row = section.rows[i];
      if (row.issues.length) throw new Error('Rett markerte opplysninger før DUV-eksport.');
      const previous = section.rows[i - 1];
      if (seen.has(row.personId) || !Number.isInteger(row.loops) || row.loops < 7 || row.kilometers < 45 ||
          !Number.isFinite(row.kilometers) || Math.abs(row.kilometers - kutcDistanceKm(row.loops)) > 1e-9 ||
          !Number.isInteger(row.rank) || row.rank < 1 || (previous && row.rank < previous.rank) ||
          (section.kind === 'los' && previous && (row.loops > previous.loops || (row.rank === previous.rank && row.loops !== previous.loops))) ||
          (section.kind === 'distance' && (row.loops !== section.loops || section.loops! < 8 || !Number.isFinite(row.timeSeconds) || row.timeSeconds! <= 0 ||
            (previous && (row.timeSeconds! < previous.timeSeconds! || (row.rank === previous.rank && row.timeSeconds !== previous.timeSeconds)))))) {
        throw new Error('Rangering eller distanse er inkonsistent. Hent resultatene på nytt.');
      }
      seen.add(row.personId);
      output.push([row.rank, row.lastName, row.firstName, row.gender, row.yearOfBirth, row.nationality,
        section.kind === 'distance' ? { value: Math.ceil(row.timeSeconds!) / 86400, format: '[h]:mm:ss' } : { value: row.kilometers, format: '0.000" km"' },
        row.birthdate ? dateCell(row.birthdate) : null, row.age ?? null, null, row.club || null, row.residence || null,
        section.kind === 'los' ? { value: row.kilometers, format: '0.0000' } : null,
        section.kind === 'los' ? { value: row.loops * 100 / 24, format: '0.000' } : null, row.nameInNonLatinCharacters || null, null]);
    }
  }
  return output;
}
